import { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { copyText } from "../lib/clipboard";
import { useTranslation } from "../i18n";
import type { CreateTokenInput, CreatedTokenResult } from "./types";

export interface NewTokenModalProps {
  open: boolean;
  currentWorkspaceId: string | null;
  currentWorkspaceName: string;
  /** Workspace slug — half of the remote MCP endpoint shown on success. */
  currentWorkspaceSlug: string;
  /** Repos the token may be narrowed to. Empty hides the repo picker. */
  repos: { id: string; slug: string }[];
  onClose: () => void;
  onSubmit: (input: CreateTokenInput) => Promise<CreatedTokenResult>;
}

interface FormState {
  name: string;
  scopeRead: boolean;
  scopeWrite: boolean;
  expiresAt: string;        // date string "YYYY-MM-DD" or ""
  scopeToWorkspace: boolean;
  repoId: string;           // "" = every repo the workspace scope allows
}

function defaultForm(scopeToWorkspace: boolean): FormState {
  return {
    name: "",
    scopeRead: false,
    scopeWrite: false,
    expiresAt: "",
    scopeToWorkspace,
    repoId: "",
  };
}

export function NewTokenModal({
  open,
  currentWorkspaceId,
  currentWorkspaceName,
  currentWorkspaceSlug,
  repos,
  onClose,
  onSubmit,
}: NewTokenModalProps) {
  const { t } = useTranslation("tokens");
  const [form, setForm] = useState<FormState>(() =>
    defaultForm(currentWorkspaceId !== null),
  );
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<CreatedTokenResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  // Reset all state when modal closes
  useEffect(() => {
    if (!open) {
      setForm(defaultForm(currentWorkspaceId !== null));
      setPending(false);
      setResult(null);
      setError(null);
      setNameError(null);
      setScopeError(null);
      setCopied(false);
      setUrlCopied(false);
    }
  }, [open, currentWorkspaceId]);

  if (!open) return null;

  async function handleSubmit() {
    // Validation
    let valid = true;
    if (!form.name.trim()) {
      setNameError(t("form.nameRequired"));
      valid = false;
    } else {
      setNameError(null);
    }

    const scopes: CreateTokenInput["scopes"] = [];
    if (form.scopeRead) scopes.push("repo:read");
    if (form.scopeWrite) scopes.push("repo:write");
    if (scopes.length === 0) {
      setScopeError(t("form.scopeRequired"));
      valid = false;
    } else {
      setScopeError(null);
    }

    if (!valid) return;

    const expiresAt = form.expiresAt
      ? new Date(`${form.expiresAt}T00:00:00Z`).toISOString()
      : null;

    // A repo scope is meaningless without the workspace scope that carries it —
    // the server refuses the pair — so it rides along with scopeToWorkspace.
    const workspaceId = form.scopeToWorkspace ? currentWorkspaceId : null;
    const input: CreateTokenInput = {
      name: form.name.trim(),
      scopes,
      expiresAt,
      workspaceId,
      repoId: workspaceId && form.repoId ? form.repoId : null,
    };

    setPending(true);
    setError(null);
    try {
      const res = await onSubmit(input);
      setResult(res);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t("error.generic");
      setError(message);
    } finally {
      setPending(false);
    }
  }

  async function handleCopy() {
    if (!result) return;
    await copyText(result.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  /** Remote MCP endpoint for the repo this token was scoped to, if any. */
  const mcpUrl = (() => {
    const repo = repos.find((r) => r.id === result?.repoId);
    if (!repo || !currentWorkspaceSlug) return null;
    return `${window.location.origin}/api/ws/${encodeURIComponent(currentWorkspaceSlug)}/r/${encodeURIComponent(repo.slug)}/mcp`;
  })();

  async function handleCopyUrl() {
    if (!mcpUrl) return;
    await copyText(mcpUrl);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  }

  // ── Success state ──────────────────────────────────────────────────────────
  if (result) {
    return (
      <Modal open={open} onClose={onClose} title={t("modal.title")}>
        <Banner kind="info" className="mb-4">
          {t("success.warning")}
        </Banner>
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            value={result.token}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-mono text-slate-100 focus:outline-none"
          />
          <Button variant="secondary" onClick={handleCopy}>
            {copied ? t("success.copied") : t("success.copy")}
          </Button>
        </div>
        {mcpUrl && (
          <div className="mb-4">
            <div className="mb-1 block text-sm font-medium text-slate-200">
              {t("success.mcpLabel")}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={mcpUrl}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-mono text-slate-100 focus:outline-none"
              />
              <Button variant="secondary" onClick={handleCopyUrl}>
                {urlCopied ? t("success.copied") : t("success.mcpCopy")}
              </Button>
            </div>
            <p className="mt-1 text-xs text-slate-500">{t("success.mcpHint")}</p>
          </div>
        )}
        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>
            {t("success.done")}
          </Button>
        </div>
      </Modal>
    );
  }

  // ── Form state ─────────────────────────────────────────────────────────────
  return (
    <Modal open={open} onClose={onClose} title={t("modal.title")}>
      {error && (
        <Banner kind="error" className="mb-4">
          {error}
        </Banner>
      )}

      {/* Name */}
      <div className="mb-4">
        <label
          htmlFor="new-token-name"
          className="mb-1 block text-sm font-medium text-slate-200"
        >
          {t("form.nameLabel")}
        </label>
        <input
          id="new-token-name"
          type="text"
          required
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100
            placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-arch-500"
          placeholder={t("form.namePlaceholder")}
        />
        {nameError && (
          <p className="mt-1 text-xs text-red-400">{nameError}</p>
        )}
      </div>

      {/* Scopes */}
      <fieldset className="mb-4">
        <legend className="mb-2 text-sm font-medium text-slate-200">{t("form.scopesLegend")}</legend>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={form.scopeRead}
            onChange={(e) => setForm((f) => ({ ...f, scopeRead: e.target.checked }))}
            aria-label={t("form.scopeReadLabel")}
          />
          {t("form.scopeReadLabel")}
        </label>
        <label className="mt-1 flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={form.scopeWrite}
            onChange={(e) => setForm((f) => ({ ...f, scopeWrite: e.target.checked }))}
            aria-label={t("form.scopeWriteLabel")}
          />
          {t("form.scopeWriteLabel")}
        </label>
        {scopeError && (
          <p className="mt-1 text-xs text-red-400">{scopeError}</p>
        )}
      </fieldset>

      {/* Expiry */}
      <div className="mb-4">
        <label
          htmlFor="new-token-expires"
          className="mb-1 block text-sm font-medium text-slate-200"
        >
          {t("form.expiresLabel")}
        </label>
        <input
          id="new-token-expires"
          type="date"
          value={form.expiresAt}
          onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
          className="rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100
            focus:outline-none focus:ring-2 focus:ring-arch-500"
        />
        <p className="mt-1 text-xs text-slate-500">{t("form.expiresHint")}</p>
      </div>

      {/* Workspace scope */}
      {currentWorkspaceId !== null && (
        <div className="mb-6">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={form.scopeToWorkspace}
              onChange={(e) =>
                setForm((f) => ({ ...f, scopeToWorkspace: e.target.checked }))
              }
              aria-label={t("form.scopeToWorkspaceAria", { name: currentWorkspaceName })}
            />
            {t("form.scopeToWorkspaceLabel", { name: currentWorkspaceName })}
          </label>

          {form.scopeToWorkspace && repos.length > 0 && (
            <div className="mt-3">
              <label
                htmlFor="new-token-repo"
                className="mb-1 block text-sm font-medium text-slate-200"
              >
                {t("form.repoLabel")}
              </label>
              <select
                id="new-token-repo"
                value={form.repoId}
                onChange={(e) => setForm((f) => ({ ...f, repoId: e.target.value }))}
                className="w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100
                  focus:outline-none focus:ring-2 focus:ring-arch-500"
              >
                <option value="">{t("form.repoAll")}</option>
                {repos.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.slug}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">{t("form.repoHint")}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {t("form.cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={pending}
          onClick={handleSubmit}
        >
          {pending ? t("form.creating") : t("form.submit")}
        </Button>
      </div>
    </Modal>
  );
}
